/**
 * Cognito Auth Service client for frontend communication.
 * 
 * This service handles authentication using Amazon Cognito User Pools.
 * It supports:
 * - Username/password authentication
 * - MFA (TOTP) verification
 * - Token refresh
 * - Password management
 * 
 * Configuration required in config.js:
 * - auth.useCognito: boolean to enable/disable Cognito
 * - auth.cognitoUserPoolId: Cognito User Pool ID
 * - auth.cognitoClientId: Cognito App Client ID
 * - auth.cognitoRegion: AWS Region
 */

import config from '../config';
import { createTrackedHeaders, CALL_TYPES } from '../utils/apiCallTracker';

// Token storage keys
const TOKEN_STORAGE_KEYS = {
  ACCESS_TOKEN: 'cognito_access_token',
  ID_TOKEN: 'cognito_id_token',
  REFRESH_TOKEN: 'cognito_refresh_token',
  USERNAME: 'cognito_username',
  EXPIRES_AT: 'cognito_expires_at'
};

class CognitoAuthService {
  constructor() {
    this.baseUrl = config.api.authServiceUrl;
    this.useCognito = config.auth?.useCognito || false;
  }

  /**
   * Creates headers with proper authentication and call type tracking
   * @param {string} token - Auth token (optional)
   * @param {string} callType - Type of call (manual/automatic)
   * @param {Object} additionalHeaders - Additional headers
   * @returns {Object} Headers object
   */
  _createHeaders(token = null, callType = CALL_TYPES.AUTOMATIC, additionalHeaders = {}) {
    const headers = {
      'Content-Type': 'application/json',
      'Accept': 'application/json',
      ...createTrackedHeaders(callType),
      ...additionalHeaders
    };

    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    return headers;
  }

  /**
   * Store tokens in localStorage
   * @param {Object} tokens - Token response from Cognito
   */
  _storeTokens(tokens, username = null) {
    if (tokens.access_token) {
      localStorage.setItem(TOKEN_STORAGE_KEYS.ACCESS_TOKEN, tokens.access_token);
    }
    if (tokens.id_token) {
      localStorage.setItem(TOKEN_STORAGE_KEYS.ID_TOKEN, tokens.id_token);
    }
    if (tokens.refresh_token) {
      localStorage.setItem(TOKEN_STORAGE_KEYS.REFRESH_TOKEN, tokens.refresh_token);
    }
    if (username) {
      localStorage.setItem(TOKEN_STORAGE_KEYS.USERNAME, username);
    }
    if (tokens.expires_in) {
      const expiresAt = Date.now() + (tokens.expires_in * 1000);
      localStorage.setItem(TOKEN_STORAGE_KEYS.EXPIRES_AT, expiresAt.toString());
    }
  }

  /**
   * Get stored access token
   * @returns {string|null}
   */
  getAccessToken() {
    return localStorage.getItem(TOKEN_STORAGE_KEYS.ACCESS_TOKEN);
  }

  /**
   * Get stored ID token
   * @returns {string|null}
   */
  getIdToken() {
    return localStorage.getItem(TOKEN_STORAGE_KEYS.ID_TOKEN);
  }

  /**
   * Get stored refresh token
   * @returns {string|null}
   */
  getRefreshToken() {
    return localStorage.getItem(TOKEN_STORAGE_KEYS.REFRESH_TOKEN);
  }

  /**
   * Get stored username
   * @returns {string|null}
   */
  getUsername() {
    return localStorage.getItem(TOKEN_STORAGE_KEYS.USERNAME);
  }

  /**
   * Check if token is expired or about to expire
   * @param {number} bufferSeconds - Buffer time before actual expiry
   * @returns {boolean}
   */
  isTokenExpired(bufferSeconds = 60) {
    const expiresAt = localStorage.getItem(TOKEN_STORAGE_KEYS.EXPIRES_AT);
    if (!expiresAt) return true;
    
    const expiryTime = parseInt(expiresAt, 10);
    const now = Date.now();
    
    return now >= (expiryTime - (bufferSeconds * 1000));
  }

  /**
   * Clear all stored tokens
   */
  clearTokens() {
    Object.values(TOKEN_STORAGE_KEYS).forEach(key => {
      localStorage.removeItem(key);
    });
  }

  // ===========================================================================
  // Authentication Methods
  // ===========================================================================

  /**
   * Login with username and password
   * @param {string} username 
   * @param {string} password 
   * @returns {Promise<Object>} Token response or MFA challenge
   */
  async login(username, password) {
    const response = await fetch(`${this.baseUrl}/auth/login`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
        ...createTrackedHeaders(CALL_TYPES.MANUAL)
      },
      body: JSON.stringify({ username, password })
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ detail: 'Login failed' }));
      throw new Error(error.detail || 'Login failed');
    }

    const data = await response.json();

    // Check if MFA is required
    if (data.mfa_required) {
      return {
        mfa_required: true,
        session: data.session,
        username: username,
        message: data.message || 'MFA verification required'
      };
    }

    // Check if MFA setup is required (user needs to configure authenticator)
    if (data.mfa_setup_required) {
      return {
        mfa_setup_required: true,
        session: data.session,
        username: username,
        message: data.message || 'MFA setup required'
      };
    }

    // Check if new password is required (first login with temp password)
    if (data.new_password_required) {
      return {
        new_password_required: true,
        session: data.session,
        username: username,
        message: data.message || 'Password change required'
      };
    }

    // Store tokens
    this._storeTokens(data, username);

    return {
      access_token: data.access_token,
      id_token: data.id_token,
      token_type: data.token_type,
      mfa_required: false
    };
  }

  /**
   * Login with form data (OAuth2 compatible endpoint)
   * @param {string} username 
   * @param {string} password 
   * @returns {Promise<Object>}
   */
  async loginFormData(username, password) {
    const params = new URLSearchParams();
    params.append('username', username);
    params.append('password', password);
    params.append('grant_type', 'password');

    const response = await fetch(`${this.baseUrl}/auth/token`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
        'Accept': 'application/json',
        ...createTrackedHeaders(CALL_TYPES.MANUAL)
      },
      body: params.toString()
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ detail: 'Login failed' }));
      throw new Error(error.detail || 'Login failed');
    }

    const data = await response.json();

    if (data.mfa_required) {
      return {
        mfa_required: true,
        session: data.session,
        username: username
      };
    }

    // Check if new password is required
    if (data.new_password_required) {
      return {
        new_password_required: true,
        session: data.session,
        username: username,
        message: data.message || 'Password change required'
      };
    }

    this._storeTokens(data, username);
    return data;
  }

  /**
   * Set new password for user with temporary password (first login)
   * @param {string} username - Username
   * @param {string} session - Session token from login response
   * @param {string} newPassword - New password to set
   * @returns {Promise<Object>}
   */
  async setNewPassword(username, session, newPassword) {
    const response = await fetch(`${this.baseUrl}/auth/set-new-password`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
        ...createTrackedHeaders(CALL_TYPES.MANUAL)
      },
      body: JSON.stringify({
        username,
        session,
        new_password: newPassword
      })
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ detail: 'Failed to set new password' }));
      throw new Error(error.detail || 'Failed to set new password');
    }

    const data = await response.json();

    // Check if MFA is now required after password change
    if (data.mfa_required) {
      return {
        mfa_required: true,
        session: data.session,
        username: username,
        message: data.message || 'MFA verification required'
      };
    }

    // Check if MFA setup is required after password change
    if (data.mfa_setup_required) {
      return {
        mfa_setup_required: true,
        session: data.session,
        username: username,
        message: data.message || 'MFA setup required'
      };
    }

    // Store tokens
    this._storeTokens(data, username);

    return {
      success: true,
      access_token: data.access_token,
      id_token: data.id_token,
      token_type: data.token_type,
      message: data.message
    };
  }

  /**
   * Initiate MFA setup during authentication flow (using session)
   * @param {string} username - Username
   * @param {string} session - Session token from login response
   * @returns {Promise<Object>} Contains secret, qr_code_uri, and new session
   */
  async setupMfaWithSession(username, session) {
    const response = await fetch(`${this.baseUrl}/auth/mfa/setup-session`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
        ...createTrackedHeaders(CALL_TYPES.MANUAL)
      },
      body: JSON.stringify({ username, session })
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ detail: 'MFA setup failed' }));
      throw new Error(error.detail || 'MFA setup failed');
    }

    return await response.json();
  }

  /**
   * Verify MFA setup and complete login (during auth flow)
   * @param {string} username - Username
   * @param {string} session - Session from setupMfaWithSession
   * @param {string} code - 6-digit TOTP code
   * @returns {Promise<Object>}
   */
  async verifyMfaSetupWithSession(username, session, code) {
    const response = await fetch(`${this.baseUrl}/auth/mfa/verify-setup-session`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
        ...createTrackedHeaders(CALL_TYPES.MANUAL)
      },
      body: JSON.stringify({
        username,
        session,
        code,
        device_name: 'AuthenticatorApp'
      })
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ detail: 'MFA verification failed' }));
      throw new Error(error.detail || 'MFA verification failed');
    }

    const data = await response.json();
    
    // Handle case where MFA setup succeeded but user needs to re-login
    // (MFA enforced at pool level - tokens not available in this flow)
    if (data.re_login_required) {
      return {
        success: false,
        re_login_required: true,
        message: data.message || 'MFA setup complete. Please log in again.'
      };
    }
    
    this._storeTokens(data, username);

    return {
      success: true,
      access_token: data.access_token,
      id_token: data.id_token,
      token_type: data.token_type,
      message: data.message
    };
  }

  /**
   * Verify MFA code and complete login
   * @param {string} session - Session token from login response
   * @param {string} code - 6-digit MFA code
   * @param {string} username - Username
   * @returns {Promise<Object>}
   */
  async verifyMfa(session, code, username) {
    const response = await fetch(`${this.baseUrl}/auth/mfa/verify`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
        ...createTrackedHeaders(CALL_TYPES.MANUAL)
      },
      body: JSON.stringify({
        session,
        code,
        username
      })
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ detail: 'MFA verification failed' }));
      throw new Error(error.detail || 'MFA verification failed');
    }

    const data = await response.json();
    this._storeTokens(data, username);

    return {
      access_token: data.access_token,
      id_token: data.id_token,
      token_type: data.token_type
    };
  }

  /**
   * Refresh access token
   * @param {string} token - Current token (optional, for compatibility)
   * @returns {Promise<Object>}
   */
  async refreshToken(token = null) {
    const refreshTokenValue = this.getRefreshToken();
    const username = this.getUsername();

    if (!refreshTokenValue) {
      throw new Error('No refresh token available');
    }

    const response = await fetch(`${this.baseUrl}/auth/refresh`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
        ...createTrackedHeaders(CALL_TYPES.AUTOMATIC)
      },
      body: JSON.stringify({
        refresh_token: refreshTokenValue,
        username: username
      })
    });

    if (!response.ok) {
      this.clearTokens();
      throw new Error('Token refresh failed');
    }

    const data = await response.json();
    this._storeTokens(data, username);

    return data;
  }

  /**
   * Logout (global sign out)
   * @param {string} token - Access token (optional)
   * @returns {Promise<void>}
   */
  async logout(token = null) {
    const accessToken = token || this.getAccessToken();

    try {
      if (accessToken) {
        await fetch(`${this.baseUrl}/auth/logout`, {
          method: 'POST',
          headers: this._createHeaders(accessToken, CALL_TYPES.MANUAL)
        });
      }
    } catch (error) {
      console.warn('Logout request failed:', error);
    } finally {
      this.clearTokens();
    }
  }

  // ===========================================================================
  // User Information Methods
  // ===========================================================================

  /**
   * Get current user information including permissions
   * @param {string} token - Access token (optional, will use stored token if not provided)
   * @param {boolean} isManual 
   * @returns {Promise<Object>}
   */
  async getCurrentUser(token = null, isManual = false) {
    const accessToken = token || this.getAccessToken();
    if (!accessToken) {
      throw new Error('Not authenticated');
    }

    // Store token if provided
    if (token && token !== this.getAccessToken()) {
      localStorage.setItem(TOKEN_STORAGE_KEYS.ACCESS_TOKEN, token);
    }

    const callType = isManual ? CALL_TYPES.MANUAL : CALL_TYPES.AUTOMATIC;

    const response = await fetch(`${this.baseUrl}/auth/me`, {
      method: 'GET',
      headers: this._createHeaders(accessToken, callType)
    });

    if (!response.ok) {
      if (response.status === 401) {
        this.clearTokens();
      }
      throw new Error('Failed to get user information');
    }

    return await response.json();
  }

  /**
   * Get current user permissions
   * @param {boolean} isManual 
   * @returns {Promise<{permissions: string[]}>}
   */
  async getUserPermissions(isManual = false) {
    const token = this.getAccessToken();
    if (!token) {
      throw new Error('Not authenticated');
    }

    const callType = isManual ? CALL_TYPES.MANUAL : CALL_TYPES.AUTOMATIC;

    const response = await fetch(`${this.baseUrl}/auth/me/permissions`, {
      method: 'GET',
      headers: this._createHeaders(token, callType)
    });

    if (!response.ok) {
      throw new Error('Failed to get user permissions');
    }

    return await response.json();
  }

  // ===========================================================================
  // MFA Management Methods
  // ===========================================================================

  /**
   * Get MFA status for current user
   * @returns {Promise<{mfa_enabled: boolean, mfa_method: string|null}>}
   */
  async getMfaStatus() {
    const token = this.getAccessToken();
    if (!token) {
      throw new Error('Not authenticated');
    }

    const response = await fetch(`${this.baseUrl}/auth/mfa/status`, {
      method: 'GET',
      headers: this._createHeaders(token, CALL_TYPES.AUTOMATIC)
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ detail: 'Failed to get MFA status' }));
      throw new Error(error.detail || 'Failed to get MFA status');
    }

    return await response.json();
  }

  /**
   * Initialize MFA setup (get TOTP secret and QR code)
   * @returns {Promise<{secret: string, qr_code_uri: string, message: string}>}
   */
  async setupMfa() {
    const token = this.getAccessToken();
    if (!token) {
      throw new Error('Not authenticated');
    }

    const response = await fetch(`${this.baseUrl}/auth/mfa/setup`, {
      method: 'POST',
      headers: this._createHeaders(token, CALL_TYPES.MANUAL)
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ detail: 'Failed to setup MFA' }));
      throw new Error(error.detail || 'Failed to setup MFA');
    }

    return await response.json();
  }

  /**
   * Enable MFA by verifying setup code
   * @param {string} code - 6-digit TOTP code
   * @param {string} deviceName - Friendly device name
   * @returns {Promise<{success: boolean, message: string}>}
   */
  async enableMfa(code, deviceName = 'AuthenticatorApp') {
    const token = this.getAccessToken();
    if (!token) {
      throw new Error('Not authenticated');
    }

    const response = await fetch(`${this.baseUrl}/auth/mfa/enable`, {
      method: 'POST',
      headers: this._createHeaders(token, CALL_TYPES.MANUAL),
      body: JSON.stringify({ code, device_name: deviceName })
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ detail: 'Failed to enable MFA' }));
      throw new Error(error.detail || 'Failed to enable MFA');
    }

    return await response.json();
  }

  /**
   * Disable MFA
   * @returns {Promise<{success: boolean, message: string}>}
   */
  async disableMfa() {
    const token = this.getAccessToken();
    if (!token) {
      throw new Error('Not authenticated');
    }

    const response = await fetch(`${this.baseUrl}/auth/mfa/disable`, {
      method: 'POST',
      headers: this._createHeaders(token, CALL_TYPES.MANUAL)
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ detail: 'Failed to disable MFA' }));
      throw new Error(error.detail || 'Failed to disable MFA');
    }

    return await response.json();
  }

  // ===========================================================================
  // Password Management Methods
  // ===========================================================================

  /**
   * Change current user's password
   * @param {string} currentPassword 
   * @param {string} newPassword 
   * @returns {Promise<{message: string}>}
   */
  async changePassword(currentPassword, newPassword) {
    const token = this.getAccessToken();
    if (!token) {
      throw new Error('Not authenticated');
    }

    const response = await fetch(`${this.baseUrl}/auth/change-password`, {
      method: 'POST',
      headers: this._createHeaders(token, CALL_TYPES.MANUAL),
      body: JSON.stringify({
        current_password: currentPassword,
        new_password: newPassword
      })
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ detail: 'Failed to change password' }));
      throw new Error(error.detail || 'Failed to change password');
    }

    return await response.json();
  }

  /**
   * Initiate forgot password flow
   * @param {string} username 
   * @returns {Promise<{message: string}>}
   */
  async forgotPassword(username) {
    const response = await fetch(`${this.baseUrl}/auth/forgot-password`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      },
      body: JSON.stringify({ username })
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ detail: 'Request failed' }));
      throw new Error(error.detail || 'Request failed');
    }

    return await response.json();
  }

  /**
   * Complete forgot password flow
   * @param {string} username 
   * @param {string} code - Verification code from email
   * @param {string} newPassword 
   * @returns {Promise<{message: string}>}
   */
  async confirmForgotPassword(username, code, newPassword) {
    const response = await fetch(`${this.baseUrl}/auth/confirm-forgot-password`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      },
      body: JSON.stringify({
        username,
        confirmation_code: code,
        new_password: newPassword
      })
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ detail: 'Password reset failed' }));
      throw new Error(error.detail || 'Password reset failed');
    }

    return await response.json();
  }

  // ===========================================================================
  // Admin User Management Methods
  // ===========================================================================

  /**
   * List all users (admin only)
   * @param {boolean} isManual 
   * @returns {Promise<{users: Object[]}>}
   */
  async listUsers(isManual = false) {
    const token = this.getAccessToken();
    if (!token) {
      throw new Error('Not authenticated');
    }

    const callType = isManual ? CALL_TYPES.MANUAL : CALL_TYPES.AUTOMATIC;

    const response = await fetch(`${this.baseUrl}/auth/admin/users`, {
      method: 'GET',
      headers: this._createHeaders(token, callType)
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ detail: 'Failed to list users' }));
      throw new Error(error.detail || 'Failed to list users');
    }

    return await response.json();
  }

  /**
   * Create a new user (admin only)
   * @param {Object} userData 
   * @returns {Promise<Object>}
   */
  async createUser(userData) {
    const token = this.getAccessToken();
    if (!token) {
      throw new Error('Not authenticated');
    }

    const response = await fetch(`${this.baseUrl}/auth/admin/users`, {
      method: 'POST',
      headers: this._createHeaders(token, CALL_TYPES.MANUAL),
      body: JSON.stringify(userData)
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ detail: 'Failed to create user' }));
      throw new Error(error.detail || 'Failed to create user');
    }

    return await response.json();
  }

  /**
   * Get a specific user (admin only)
   * @param {string} username 
   * @returns {Promise<Object>}
   */
  async getUser(username) {
    const token = this.getAccessToken();
    if (!token) {
      throw new Error('Not authenticated');
    }

    const response = await fetch(`${this.baseUrl}/auth/admin/users/${username}`, {
      method: 'GET',
      headers: this._createHeaders(token, CALL_TYPES.AUTOMATIC)
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ detail: 'Failed to get user' }));
      throw new Error(error.detail || 'Failed to get user');
    }

    return await response.json();
  }

  /**
   * Update user permissions (admin only)
   * @param {string} username 
   * @param {string[]} permissions 
   * @returns {Promise<Object>}
   */
  async updateUserPermissions(username, permissions) {
    const token = this.getAccessToken();
    if (!token) {
      throw new Error('Not authenticated');
    }

    const response = await fetch(`${this.baseUrl}/auth/admin/users/${username}/permissions`, {
      method: 'PUT',
      headers: this._createHeaders(token, CALL_TYPES.MANUAL),
      body: JSON.stringify({ permissions })
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ detail: 'Failed to update permissions' }));
      throw new Error(error.detail || 'Failed to update permissions');
    }

    return await response.json();
  }

  /**
   * Delete a user (admin only)
   * @param {string} username 
   * @returns {Promise<Object>}
   */
  async deleteUser(username) {
    const token = this.getAccessToken();
    if (!token) {
      throw new Error('Not authenticated');
    }

    const response = await fetch(`${this.baseUrl}/auth/admin/users/${username}`, {
      method: 'DELETE',
      headers: this._createHeaders(token, CALL_TYPES.MANUAL)
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ detail: 'Failed to delete user' }));
      throw new Error(error.detail || 'Failed to delete user');
    }

    return await response.json();
  }

  /**
   * Disable a user (admin only)
   * @param {string} username 
   * @returns {Promise<Object>}
   */
  async disableUser(username) {
    const token = this.getAccessToken();
    if (!token) {
      throw new Error('Not authenticated');
    }

    const response = await fetch(`${this.baseUrl}/auth/admin/users/${username}/disable`, {
      method: 'POST',
      headers: this._createHeaders(token, CALL_TYPES.MANUAL)
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ detail: 'Failed to disable user' }));
      throw new Error(error.detail || 'Failed to disable user');
    }

    return await response.json();
  }

  /**
   * Enable a user (admin only)
   * @param {string} username 
   * @returns {Promise<Object>}
   */
  async enableUser(username) {
    const token = this.getAccessToken();
    if (!token) {
      throw new Error('Not authenticated');
    }

    const response = await fetch(`${this.baseUrl}/auth/admin/users/${username}/enable`, {
      method: 'POST',
      headers: this._createHeaders(token, CALL_TYPES.MANUAL)
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ detail: 'Failed to enable user' }));
      throw new Error(error.detail || 'Failed to enable user');
    }

    return await response.json();
  }

  // ===========================================================================
  // Health Check
  // ===========================================================================

  /**
   * Check service health
   * @returns {Promise<Object>}
   */
  async checkHealth() {
    const response = await fetch(`${this.baseUrl}/auth/health`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        ...createTrackedHeaders(CALL_TYPES.AUTOMATIC)
      }
    });

    if (!response.ok) {
      throw new Error('Auth service is not healthy');
    }

    return await response.json();
  }

  // ===========================================================================
  // Activity Tracking
  // ===========================================================================

  /**
   * Record a navigation event
   * @param {Object} navigationData 
   * @returns {Promise<void>}
   */
  async recordNavigation(navigationData) {
    const token = this.getAccessToken();
    if (!token) return;

    try {
      await fetch(`${this.baseUrl}/auth/navigation`, {
        method: 'POST',
        headers: this._createHeaders(token, CALL_TYPES.AUTOMATIC),
        body: JSON.stringify(navigationData)
      });
    } catch (error) {
      console.warn('Navigation tracking failed:', error);
    }
  }

  /**
   * Get user activity history
   * @param {number} limit 
   * @returns {Promise<Object>}
   */
  async getUserActivity(limit = 50) {
    const token = this.getAccessToken();
    if (!token) {
      throw new Error('Not authenticated');
    }

    const response = await fetch(`${this.baseUrl}/auth/activity?limit=${limit}`, {
      method: 'GET',
      headers: this._createHeaders(token, CALL_TYPES.AUTOMATIC)
    });

    if (!response.ok) {
      throw new Error('Failed to get activity');
    }

    return await response.json();
  }
}

// Export singleton instance
const cognitoAuthService = new CognitoAuthService();
export default cognitoAuthService;
